import * as React from 'react'
import { SearchTextInput } from '../components/SearchTextInput'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import { SearchResults } from '../components/SearchResults/SearchResults'
import RecentSearches from '../components/RecentSearches/RecentSearches'
import { useData, useHandlers } from './hooks'
import Box from '@mui/material/Box'
import { useFilterChipData } from '@user/components/HereMap/hooks/useFilterChipData'

import { HereResult } from '@user/store/services/hereGeoCoder'

type Props = {
  map: H.Map | null
  onClose: () => void
  onFoundLocation: () => void
  onPanToLocation?: (result: HereResult) => Promise<void>
}

export function SearchDrawerContents(props: Props): JSX.Element {
  const {
    searchText,
    setSearchText,
    addRecentSearch,
    clearRecentSearches,
    recentSearches,
  } = useData()
  const {
    handleSearchTextChange,
    results,
    handleRecentClick,
    handleResultClick,
    handleEnterPress,
  } = useHandlers({
    addRecentSearch,
    clearRecentSearches,
    recentSearches,
    setSearchText,
    map: props.map,
    onClose: props.onClose,
    onFoundLocation: props.onFoundLocation,
    onPanToLocation: props.onPanToLocation,
  })

  // Check if filters are active. selectedTypes/myRecords come from the shared
  // useFilterChipData source of truth, so myRecords already reflects the
  // salesperson-filter override (my-records is treated as inactive when that
  // filter neutralises it in the fetch).
  const { selectedTypes, myRecords } = useFilterChipData()

  // Filters are active if not all three types are selected OR myRecords is true
  const allTypes = ['draftlead', 'lead', 'opportunity']
  const hasAllTypesSelected =
    selectedTypes.length === 3 &&
    allTypes.every((type) => selectedTypes.includes(type))
  const filtersActive = !hasAllTypesSelected || !myRecords

  return (
    <Stack direction={'column'} height="100%" spacing={2}>
      <Stack p={0} spacing={1.2}>
        {filtersActive && (
          <Box px={2}>
            <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
              You have filters applied. All search results will display
              regardless of your filter selections.
            </Alert>
          </Box>
        )}
        <Box px={1}>
          <SearchTextInput
            placeholder="Search by address, city, state or postal code"
            value={searchText}
            onChange={handleSearchTextChange}
            onEnterPress={handleEnterPress}
          />
        </Box>
        <Box px={2}>
          <SearchResults
            results={results}
            onResultClick={handleResultClick}
            touched={searchText.length > 0}
          />
        </Box>
        <Divider />
        <Box px={2}>
          <RecentSearches
            recentSearches={recentSearches}
            clearRecentSearches={clearRecentSearches}
            onClick={handleRecentClick}
          />
        </Box>
      </Stack>
    </Stack>
  )
}
